import { DEFAULT_PUSHUP_RULE, type CurlRule, type PushupRule } from './accessories.ts';
import type { ConfigSnapshot } from './events.ts';
import { DEFAULT_KG_GYM, DEFAULT_LB_GYM } from './gyms.ts';
import type { MainLift } from './lifts.ts';
import { mass, toDisplay, type Mass, type Unit } from './mass.ts';
import type { Gym } from './plates.ts';
import type { LiftRule } from './progression.ts';
import { DEFAULT_TIMING, type SessionTiming } from './session.ts';

/**
 * Everything a user can change about their plan (journey J6). Stored as a complete snapshot in a
 * `settings_updated` event (ADR-0015); every workout snapshots the config it used, so changes apply forward only.
 */
export interface Settings {
  unit: Unit;
  gym: Gym;
  lifts: Record<MainLift, LiftRule>;
  rest: { successS: number; missS: number; afterWarmupS: number; accessoryS: number };
  curl: CurlRule & { enabled: boolean };
  pushup: PushupRule & { enabled: boolean };
}

const LIFTS: MainLift[] = ['squat', 'bench', 'row', 'ohp', 'deadlift'];

export function defaultSettings(unit: Unit = 'kg'): Settings {
  const kg = unit === 'kg';
  const step = kg ? mass(2.5) : mass(5);
  const rule = (inc: Mass, after?: Mass): LiftRule => ({ increment: inc, ...(after ? { incrementAfterFirstMiss: after } : {}), deloadAfter: 3, deloadFraction: 0.1 });
  return {
    unit,
    gym: kg ? DEFAULT_KG_GYM : DEFAULT_LB_GYM,
    lifts: { squat: rule(step), bench: rule(step), row: rule(step), ohp: rule(step), deadlift: rule(kg ? mass(5) : mass(10), step) },
    rest: { successS: DEFAULT_TIMING.restSuccessS, missS: DEFAULT_TIMING.restMissS, afterWarmupS: DEFAULT_TIMING.restAfterWarmupS, accessoryS: DEFAULT_TIMING.accessoryRestS },
    curl: { enabled: false, sets: 3, reps: 10, dumbbells: (kg ? [4, 6, 8, 10, 12, 14, 16, 18, 20, 22, 24] : [10, 15, 20, 25, 30, 35, 40, 45, 50]).map(mass) },
    pushup: { enabled: false, ...DEFAULT_PUSHUP_RULE },
  };
}

export interface SettingsError {
  field: string;
  message: string;
}

/** Reject impossible or dangerous values before anything is written (ADR-0015). Ranges are per unit. */
export function validateSettings(s: Settings): SettingsError[] {
  const errors: SettingsError[] = [];
  const kg = s.unit === 'kg';
  const u = kg ? 'kg' : 'lb';
  const f = (m: Mass) => `${toDisplay(m)} ${u}`;
  const range = (field: string, v: number, lo: number, hi: number, label: string, fmt: (x: number) => string = String) => {
    if (!Number.isFinite(v) || v < lo || v > hi) errors.push({ field, message: `${label} must be between ${fmt(lo)} and ${fmt(hi)}.` });
  };
  const int = (field: string, v: number, label: string) => {
    if (!Number.isInteger(v)) errors.push({ field, message: `${label} must be a whole number.` });
  };

  range('gym.bar', s.gym.bar, mass(kg ? 5 : 10), mass(kg ? 30 : 65), 'Bar weight', f);
  range('gym.collar', s.gym.collar, 0, mass(kg ? 5 : 10), 'Each collar', f);
  if (s.gym.plates.length === 0) errors.push({ field: 'gym.plates', message: 'Add at least one plate size.' });
  const seen = new Set<number>();
  s.gym.plates.forEach((p, i) => {
    range(`gym.plates.${i}.weight`, p.weight, mass(kg ? 0.25 : 0.5), mass(kg ? 50 : 100), 'Plate weight', f);
    range(`gym.plates.${i}.perSide`, p.perSide, 0, 20, 'Plates per side');
    int(`gym.plates.${i}.perSide`, p.perSide, 'Plates per side');
    if (seen.has(p.weight)) errors.push({ field: `gym.plates.${i}.weight`, message: `${f(p.weight)} is listed twice.` });
    seen.add(p.weight);
  });
  for (const l of LIFTS) {
    const r = s.lifts[l];
    range(`lifts.${l}.increment`, r.increment, mass(kg ? 0.5 : 1), mass(kg ? 10 : 20), 'Increment', f);
    if (r.incrementAfterFirstMiss !== undefined) range(`lifts.${l}.incrementAfterFirstMiss`, r.incrementAfterFirstMiss, mass(kg ? 0.5 : 1), mass(kg ? 10 : 20), 'Increment after a miss', f);
    range(`lifts.${l}.deloadAfter`, r.deloadAfter, 1, 10, 'Misses before a deload');
    int(`lifts.${l}.deloadAfter`, r.deloadAfter, 'Misses before a deload');
    range(`lifts.${l}.deloadFraction`, r.deloadFraction, 0.05, 0.5, 'Deload amount', (x) => `${Math.round(x * 100)}%`);
    if (r.scheme && !SCHEMES[l].some((s) => s.sets === r.scheme!.sets && s.reps === r.scheme!.reps)) errors.push({ field: `lifts.${l}.scheme`, message: 'Pick one of the listed set schemes.' });
  }
  range('rest.successS', s.rest.successS, 30, 900, 'Rest after a good set', (x) => `${x} s`);
  range('rest.missS', s.rest.missS, 30, 900, 'Rest after a missed set', (x) => `${x} s`);
  range('rest.afterWarmupS', s.rest.afterWarmupS, 0, 600, 'Rest after warm-ups', (x) => `${x} s`);
  range('rest.accessoryS', s.rest.accessoryS, 0, 600, 'Rest between accessory sets', (x) => `${x} s`);
  range('curl.sets', s.curl.sets, 1, 10, 'Curl sets');
  range('curl.reps', s.curl.reps, 1, 30, 'Curl reps');
  if (s.curl.enabled && s.curl.dumbbells.length === 0) errors.push({ field: 'curl.dumbbells', message: 'Add the dumbbell weights you have.' });
  range('pushup.repStep', s.pushup.repStep, 1, 5, 'Reps added per session');
  range('pushup.maxReps', s.pushup.maxReps, 5, 100, 'Rep ceiling');
  range('pushup.resetReps', s.pushup.resetReps, 1, 99, 'Reps after adding a set');
  if (s.pushup.resetReps >= s.pushup.maxReps) errors.push({ field: 'pushup.resetReps', message: 'Reps after adding a set must be below the rep ceiling.' });
  range('pushup.maxSets', s.pushup.maxSets, 1, 10, 'Most push-up sets');
  return errors;
}

/** Set schemes a lift may use (ADR-0019). The first is the programme default. */
export const SCHEMES: Record<MainLift, readonly { sets: number; reps: number }[]> = {
  squat: [{ sets: 5, reps: 5 }, { sets: 3, reps: 5 }, { sets: 3, reps: 3 }],
  bench: [{ sets: 5, reps: 5 }, { sets: 3, reps: 5 }, { sets: 3, reps: 3 }],
  row: [{ sets: 5, reps: 5 }, { sets: 3, reps: 5 }, { sets: 3, reps: 3 }],
  ohp: [{ sets: 5, reps: 5 }, { sets: 3, reps: 5 }, { sets: 3, reps: 3 }],
  deadlift: [{ sets: 1, reps: 5 }, { sets: 1, reps: 3 }],
};

/** What a workout snapshots (ADR-0002): only enabled accessories travel with it. */
export function configFrom(s: Settings): ConfigSnapshot {
  const { enabled: curlOn, ...curl } = s.curl;
  const { enabled: pushOn, ...pushup } = s.pushup;
  return { gym: s.gym, rules: s.lifts, timing: timingFrom(s), ...(curlOn ? { curl } : {}), ...(pushOn ? { pushup } : {}) };
}

export function timingFrom(s: Settings): SessionTiming {
  return { ...DEFAULT_TIMING, restSuccessS: s.rest.successS, restMissS: s.rest.missS, restAfterWarmupS: s.rest.afterWarmupS, accessoryRestS: s.rest.accessoryS };
}

/** Gym presets for onboarding (J1 step 7). */
export const GYM_PRESETS: Record<'standard_kg' | 'standard_lb' | 'home_kg', { label: string; gym: Gym }> = {
  standard_kg: { label: 'Standard gym: 20 kg bar, full plate set', gym: DEFAULT_KG_GYM },
  standard_lb: { label: 'Standard gym: 45 lb bar, full plate set', gym: DEFAULT_LB_GYM },
  home_kg: {
    label: 'Home gym: 20 kg bar, a basic set (edit below)',
    gym: { unit: 'kg', bar: mass(20), collar: 0, plates: [20, 10, 5, 2.5, 1.25].map((w) => ({ weight: mass(w), perSide: w >= 10 ? 1 : 2 })) },
  },
};

/**
 * Settings after a kg ↔ lb switch (ADR-0018): increments and dumbbells come from the new unit's defaults (2.5 kg
 * is not a pound plate), and the gym is the one the user picks; personal choices carry over (rest, deload rule,
 * accessories on or off, the push-up ladder).
 */
export function settingsForUnit(s: Settings, to: Unit, gym: Gym): Settings {
  const d = defaultSettings(to);
  const lifts = Object.fromEntries(LIFTS.map((l) => [l, { ...d.lifts[l], deloadAfter: s.lifts[l].deloadAfter, deloadFraction: s.lifts[l].deloadFraction, ...(s.lifts[l].scheme ? { scheme: s.lifts[l].scheme } : {}) }])) as Settings['lifts'];
  return { ...s, unit: to, gym: { ...gym, unit: to }, lifts, curl: { ...d.curl, enabled: s.curl.enabled, sets: s.curl.sets, reps: s.curl.reps }, pushup: s.pushup };
}
